import React, { useEffect, useMemo } from 'react';
import useMedicalProfileData, { GroupedResource } from '../../../../hooks/api/useMedicalProfileData';
import SelectLoader from './SelectLoader';
import ResourceSearch from '../../ResourceSearch/ResourceSearch';
import ResourceListCard from '../../ResourceSearch/ResourceListCard';
import { getIntl } from '../../../../utils/intl';
import useMedicalProfile from '../../../../hooks/api/useMedicalProfile';
import { pluralResourceCategoryMessages, resourceCategoryMessages } from '../../../../utils/sharedMessages';
import getName from '../../../../utils/getName';
import ResourceCard from '../../ResourceCard';
import { useNestedNavigation } from '../../../../utils/appNavigationNames';
import {
    NoteDestination,
    ResourceDomainNoteDestination,
    isResourceCategoryNoteDestination,
    isResourceDomainNoteDestination,
    isResourceNoteDestination,
    resourceCategoryToResourceNoteEntityTypeV2,
    resourceNoteEntityTypeToResourceCategory,
} from '../utils/types';
import { useResourceCategory } from '../../../../hooks/api/useResource';
import { MedicalDataTypes } from '../../../../primary-record-types';
import Stack from '../../../v2BaseComponents/Stack';
import Text from '../../../v2BaseComponents/Text';

const intl = getIntl('resource-select', {
    header: 'Select a {category} to move the note to (optional)',
    subHeader:
        "If no {category} is selected, the note will be moved to the {categories} section of {profileName}'s profile.",
    selectedSubText: 'Selected {category}',
});

export type ResourceSelectOwnProps = {
    value: ResourceDomainNoteDestination;
    onChange: (value: NoteDestination) => void;
    isDisabled: boolean;
};

export type ResourceSelectProps = ResourceSelectOwnProps;

export const shouldRenderResourceSelect = (value: NoteDestination): value is ResourceDomainNoteDestination => {
    return isResourceDomainNoteDestination(value);
};

const getGroupResourceFromId = (
    groupedResources: GroupedResource[],
    resourceId: string,
): GroupedResource | undefined => {
    return groupedResources.find((_) => _.id === resourceId || _.groupSubResources?.find((_) => _.id === resourceId));
};

/**
 * Determines the resource category for a resource category or resource note.
 *
 * There's some trickiness here.  If they select a resource category, we load the resources If they
 * then select a resource, we don't want to do a refetch to get the resource category.  It'd be a jarring
 * UX.  This leverages a cache to get the resource category from the resource but still will do a fresh load
 * if the user loads the component with a resource (not the category) selected.  They would not have any cached
 * resources in that case.
 */
const useCategory = (
    value: ResourceDomainNoteDestination,
): { isLoading: boolean; resourceCategory: MedicalDataTypes.PrimaryRecordResourceCategory | null } => {
    /**
     * Always load a resource category for a resource ID.  This may not be used if we find the data in the cache,
     * but it's better to load it and not need it than to not load it and need it.
     */
    const directlyLoadedResourceCategoryReturnValue = useResourceCategory({
        medicalProfileId: value.medicalProfileId,
        resourceId: isResourceNoteDestination(value) ? value.entityId : null,
    });
    /**
     * If a grouped resource is loaded, hang onto it to preemptively load the resource category
     */
    const [cachedGroupedResources, setCachedGroupedResources] = React.useState<GroupedResource[]>([]);
    const { groupedResources } = useMedicalProfileData({
        medicalProfileId: value.medicalProfileId,
        resourceCategory: isResourceCategoryNoteDestination(value) ? value.entityType : undefined,
    });
    useEffect(() => {
        const groupedResourcesNotCached = groupedResources.filter(
            (_) => !cachedGroupedResources.find((__) => __.id === _.id),
        );
        if (groupedResourcesNotCached.length) {
            setCachedGroupedResources([...cachedGroupedResources, ...groupedResourcesNotCached]);
        }
    }, [cachedGroupedResources, groupedResources]);
    const maybeResourceFromCache = useMemo(() => {
        if (!isResourceNoteDestination(value)) return null;

        return getGroupResourceFromId(cachedGroupedResources, value.entityId);
    }, [cachedGroupedResources, value]);
    const resourceCategory = useMemo(() => {
        if (isResourceCategoryNoteDestination(value)) return value.entityType;

        if (isResourceNoteDestination(value)) return resourceNoteEntityTypeToResourceCategory(value.entityType);

        return directlyLoadedResourceCategoryReturnValue.resourceCategory || maybeResourceFromCache?.type || null;
    }, [directlyLoadedResourceCategoryReturnValue.resourceCategory, maybeResourceFromCache?.type, value]);

    return {
        isLoading: !resourceCategory,
        resourceCategory,
    };
};

const ResourceSelect: React.FC<ResourceSelectProps> = ({ value, onChange, isDisabled }) => {
    const { nestedProfileNavigation } = useNestedNavigation();
    const { profile, isLoadingProfile } = useMedicalProfile({
        medicalProfileId: value.medicalProfileId,
    });
    const { isLoading: isLoadingResourceCategory, resourceCategory } = useCategory(value);

    // technically a reuse of the useCategory hook, but react-query uses a cache so it's not a big deal
    const { isLoadingResources, groupedResources } = useMedicalProfileData({
        medicalProfileId: value.medicalProfileId,
        resourceCategory: resourceCategory || undefined,
    });

    const maybeInvalidValue = useMemo<string | null | 'invalid-value'>(() => {
        if (isLoadingResources || isLoadingResourceCategory) return null;

        if (isResourceCategoryNoteDestination(value)) return null;

        const targetResourceId = getGroupResourceFromId(groupedResources, value.entityId)?.id;

        return targetResourceId || 'invalid-value';
    }, [isLoadingResources, isLoadingResourceCategory, value, groupedResources]);

    useEffect(() => {
        if (maybeInvalidValue === 'invalid-value') {
            onChange({
                entityType: 'medical-profile',
                medicalProfileId: value.medicalProfileId,
            });
        }
    }, [maybeInvalidValue, onChange, value.medicalProfileId]);

    const targetResource = useMemo(() => {
        if (isLoadingResources) return null;

        if (isResourceCategoryNoteDestination(value)) return null;

        return getGroupResourceFromId(groupedResources, value.entityId);
    }, [groupedResources, isLoadingResources, value]);

    if (isLoadingResources || isLoadingProfile || maybeInvalidValue === 'invalid-value' || !resourceCategory)
        return <SelectLoader />;

    return (
        <Stack vertical fullWidth space={1}>
            <Stack vertical fullWidth space={0}>
                <Text variant="subtitle-2">
                    {intl('header', {
                        category: resourceCategoryMessages(resourceCategory),
                    })}
                </Text>
                <Text variant="caption-1">
                    {intl('subHeader', {
                        category: resourceCategoryMessages(resourceCategory).toLocaleLowerCase(),
                        categories: pluralResourceCategoryMessages(resourceCategory).toLocaleLowerCase(),
                        // @ts-expect-error TS18047
                        profileName: getName(profile.contactDetails),
                    })}
                </Text>
                {isResourceNoteDestination(value) || !targetResource ? null : (
                    <Stack vertical fullWidth space={0.5} style={{ marginTop: 2 }}>
                        <ResourceCard
                            resource={targetResource}
                            category={targetResource.type}
                            profileId={value.medicalProfileId}
                            onPress={() => {
                                nestedProfileNavigation('data-details', {
                                    profileId: value.medicalProfileId,
                                    category: targetResource.type,
                                    resourceId: targetResource.id,
                                });
                            }}
                        />
                        <Text variant="caption-1">
                            {intl('selectedSubText', {
                                category: resourceCategoryMessages(targetResource.type),
                            })}
                        </Text>
                    </Stack>
                )}
            </Stack>
            <ResourceSearch
                resources={groupedResources}
                category={resourceCategory}
                isLoadingResources={false}
                renderFilteredResources={(resources) => {
                    const selected = maybeInvalidValue === resources.id;
                    return (
                        <ResourceListCard
                            resource={resources}
                            isSelected={selected}
                            isSelectable={true}
                            category={resources.type}
                            profileId={value.medicalProfileId}
                            onSelect={() => {
                                if (selected) {
                                    onChange({
                                        entityType: resourceCategory,
                                        medicalProfileId: value.medicalProfileId,
                                    });
                                } else {
                                    onChange({
                                        entityType: resourceCategoryToResourceNoteEntityTypeV2(resources.type),
                                        medicalProfileId: value.medicalProfileId,
                                        entityId: resources.id,
                                    });
                                }
                            }}
                            isDisabled={isDisabled}
                        />
                    );
                }}
            />
        </Stack>
    );
};

export default React.memo(ResourceSelect);
